import { toast as sonnerToast } from "sonner";
import { t } from "i18next";

const DEFAULT_TTL_MS = 2000;
const DEFAULT_TTL_ERROR_MS = 4000;

const DEFAULT_ERROR_MESSAGE = "Oops, something went wrong";

/**
 * Optional action link rendered under the toast message (Linear-style),
 * e.g. "View job" that navigates the user to a newly created resource.
 */
export interface ToastAction {
	label: string;
	onClick: () => void;
}

export interface ToastOptions {
	/** Auto-dismiss time in ms. */
	ttl?: number;
	/** Secondary muted line shown under the title. */
	description?: string;
	/** Optional link rendered under the description. */
	action?: ToastAction;
	/**
	 * Id of an existing toast to replace in place. Pass the id returned by
	 * {@link toast.loading} so the spinner turns into the outcome instead of a
	 * second toast stacking below it.
	 */
	id?: ToastId;
}

/** Handle to a rendered toast, returned by {@link toast.loading}. */
export type ToastId = string | number;

/** Legacy call sites pass a plain ttl number; newer ones pass an options object. */
type ToastArg = number | ToastOptions;

/**
 * Render the description column. When an action is supplied it is shown as a
 * left-aligned link beneath the (optional) description text, matching the
 * Linear toast layout.
 */
function buildDescription({ description, action }: ToastOptions): React.ReactNode | undefined {
	if (!action) return description;

	return (
		<div className="flex flex-col items-start gap-1.5">
			{description && <span>{description}</span>}
			<button
				type="button"
				onClick={action.onClick}
				className="w-fit text-left text-sm font-medium text-blue-600 hover:underline"
			>
				{action.label}
			</button>
		</div>
	);
}

/** Turn the legacy-or-new argument into sonner's options. */
function toSonnerOptions(arg: ToastArg | undefined, defaultTtl: number) {
	const opts: ToastOptions = typeof arg === "number" ? { ttl: arg } : arg ?? {};
	return {
		duration: opts.ttl ?? defaultTtl,
		description: buildDescription(opts),
		id: opts.id,
	};
}

/**
 * App-wide toast helper backed by sonner. Keeps the same API surface as the
 * old MobX ToastStore so call sites read the same (`toast.success(...)`),
 * but can be used from anywhere — components, hooks and plain modules alike.
 *
 * The second argument accepts either a `ttl` (ms) for backwards compatibility
 * or a `ToastOptions` object with `description` and an optional `action` link.
 */
export const toast = {
	success(text: string, opts?: ToastArg) {
		sonnerToast.success(text, toSonnerOptions(opts, DEFAULT_TTL_MS));
	},

	successGeneric(ttl = DEFAULT_TTL_MS) {
		sonnerToast.success(t("translation:SAVE_SUCCESS_GENERIC"), { duration: ttl });
	},

	errorGeneric(ttl = DEFAULT_TTL_MS) {
		sonnerToast.error(t("translation:SOMETHING_WENT_WRONG"), { duration: ttl });
	},

	errorGenericObRule(message: string, ttl = DEFAULT_TTL_MS) {
		sonnerToast.error(message, { duration: ttl });
	},

	apiError(errorCode: string, ttl = DEFAULT_TTL_ERROR_MS) {
		const description = t(("translation:error." + errorCode) as any);

		if (description) {
			this.error(description, ttl);
		} else {
			this.errorGeneric(ttl);
		}
	},

	info(text: string, opts?: ToastArg) {
		sonnerToast.info(text, toSonnerOptions(opts, DEFAULT_TTL_MS));
	},

	warning(text: string, opts?: ToastArg) {
		sonnerToast.warning(text, toSonnerOptions(opts, DEFAULT_TTL_ERROR_MS));
	},

	error(text?: string, opts?: ToastArg) {
		sonnerToast.error(text || DEFAULT_ERROR_MESSAGE, toSonnerOptions(opts, DEFAULT_TTL_ERROR_MS));
	},

	/**
	 * Shows a spinner that stays until you resolve it. Sonner never auto-closes
	 * a loading toast — it returns early from its auto-close effect for
	 * `type === "loading"` whatever `duration` says — so the returned id MUST be
	 * handed to a later `success`/`error`/`dismiss` call, including on the error
	 * path. Dropping it leaves the spinner on screen forever.
	 *
	 * ```ts
	 * const toastId = toast.loading(t("saving"));
	 * try {
	 *   await save();
	 *   toast.success(t("saved"), { id: toastId });
	 * } catch {
	 *   toast.error(t("failed"), { id: toastId });
	 * }
	 * ```
	 */
	loading(text: string, opts?: ToastArg): ToastId {
		return sonnerToast.loading(text, toSonnerOptions(opts, DEFAULT_TTL_MS));
	},

	/** Removes a toast by id, or all of them when called without one. */
	dismiss(id?: ToastId) {
		sonnerToast.dismiss(id);
	},

	/**
	 * Helper to quickly display feedback message based on boolean.
	 */
	genericSuccessOrError(success: boolean) {
		if (success) {
			this.successGeneric();
		} else {
			this.errorGeneric();
		}
	},
};
